/**
 * Helpers for admin API errors (sheen-BE JSON: success, error, message, errors[], code, permissionKey).
 */
import {
  extractSheenBackendErrorMessage,
  extractSheenBackendErrorMessageLoose,
} from '../../utils/apiErrorMessage';
import { hasAccess } from '../constants/teamAccessPermissions';

/**
 * Team members often hit GET /admin/users without Users|View (e.g. notification feed).
 * Do not show the global "Access Restricted" toast for that expected case.
 */
/** Backend route still admin-only while panel permission is ON (DELETE products, etc.). */
export function isTeamRouteAuthorizationError(errMsg) {
  if (typeof errMsg !== 'string') return false;
  return /role\s+team\s+is\s+not\s+authorized\s+to\s+access\s+this\s+route/i.test(errMsg);
}

export function shouldSuppressAdminAccessDeniedToast(permissionKey, errMsg) {
  if (permissionKey === 'Users|View') return true;
  if (permissionKey === 'Analytics|View') return true;
  if (typeof errMsg === 'string' && /permission to view users/i.test(errMsg)) return true;
  if (typeof errMsg === 'string' && /permission to view analytics/i.test(errMsg)) return true;
  if (typeof errMsg === 'string' && /\/admin\/analytics/i.test(errMsg)) return true;
  if (isTeamRouteAuthorizationError(errMsg)) return true;
  const u = typeof window !== 'undefined' ? (() => {
    try {
      const s = localStorage.getItem('adminUser');
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  })() : null;
  if (u?.role === 'team' && typeof errMsg === 'string') {
    if (/analytics/i.test(errMsg) && /denied|forbidden|restricted|not authorized/i.test(errMsg)) {
      return true;
    }
  }
  if (
    u?.role === 'team' &&
    permissionKey &&
    typeof permissionKey === 'string' &&
    hasAccess(permissionKey, u)
  ) {
    return true;
  }
  return false;
}

/** Parse Error thrown by adminApiRequest (payload is JSON.stringify in .message). */
export function parseThrownAdminError(error) {
  const raw = error?.message ?? '';
  try {
    const o = JSON.parse(raw);
    const message =
      extractSheenBackendErrorMessageLoose(o, o.status) ||
      raw ||
      'Unknown error';
    return {
      message,
      code: o.code,
      status: o.status,
      permissionKey: o.permissionKey,
    };
  } catch {
    return { message: raw || 'Unknown error' };
  }
}

/** Read JSON body from a non-OK fetch Response (safe if not JSON). */
export async function parseFetchErrorResponse(response) {
  let body = {};
  try {
    const ct = response.headers.get('content-type');
    if (ct && ct.includes('application/json')) {
      body = await response.json();
    }
  } catch {
    /* ignore */
  }
  const message = extractSheenBackendErrorMessageLoose(body, response.status);
  return { body, message };
}

/**
 * Team permission denied (403) — show global admin toast.
 * Skip when backend returns CANNOT_DELETE_ADMIN (business rule, not team ACL).
 */
export function dispatchAdminAccessDeniedIfNeeded(body, fallbackMessage) {
  if (body && body.code === 'CANNOT_DELETE_ADMIN') return;
  const errMsg =
    (typeof fallbackMessage === 'string' && fallbackMessage.trim()
      ? fallbackMessage.trim()
      : null) ||
    (body && typeof body === 'object'
      ? extractSheenBackendErrorMessage(body, 403)
      : null) ||
    'Access denied';
  const permissionKey = body && body.permissionKey != null ? body.permissionKey : null;
  if (shouldSuppressAdminAccessDeniedToast(permissionKey, errMsg)) return;
  window.dispatchEvent(
    new CustomEvent('adminAccessDenied', {
      detail: { error: errMsg, permissionKey },
    })
  );
}
